'use client';

import React, { type ElementType } from 'react';
import {
  useUser,
  useSkinAnalysisService,
  useProductService,
  useReviewService,
  createReviewStatsMap,
  getCartRoutineProductIds,
  getCartAddProduct,
  type Currency,
  orderProductsByKeys,
} from '@skin-seoul/service';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { CartRoutineProducts } from './CartRoutineProducts';
import { CartRecommendationsSkeleton } from './CartRecommendations.Skeleton';

type Props = {
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
  onCartMutation?: (mutation: Promise<boolean>) => void;
};

export function CartSkinAnalysisRoutine(props: Props) {
  const { user, isAuthenticated, isLoading } = useUser();
  if (isLoading || !isAuthenticated || !user) {
    return null;
  }
  return (
    <QueryBoundary key={JSON.stringify([user.userId, props.currency])} errorFallback={<></>}>
      <RoutineOverview {...props} userId={user.userId} />
    </QueryBoundary>
  );
}

function RoutineOverview(props: Props & { userId: string | number }) {
  const { useSuspenseSkinAnalysisMyOverview } = useSkinAnalysisService();
  const overview = useSuspenseSkinAnalysisMyOverview(props.userId);
  const resultId = overview.data.routine?.resultId;
  if (overview.isError || !resultId) {
    return null;
  }
  return <RoutineCandidates {...props} resultId={resultId} />;
}

function RoutineCandidates(props: Props & { resultId: string }) {
  const { useSkinAnalysisRecommendationProducts } = useSkinAnalysisService();
  const query = useSkinAnalysisRecommendationProducts({ resultId: props.resultId, currency: props.currency });
  if (query.isError) {
    return null;
  }
  if (!query.data) {
    return <CartRecommendationsSkeleton />;
  }
  const ids = getCartRoutineProductIds(query.data);
  if (!ids.length) {
    return null;
  }
  return (
    <QueryBoundary loadingFallback={<CartRecommendationsSkeleton />} errorFallback={<></>}>
      <RoutineCatalog {...props} ids={ids} />
    </QueryBoundary>
  );
}

function RoutineCatalog(props: Props & { ids: number[] }) {
  const { useGetSuspenseProductsByIds } = useProductService();
  const query = useGetSuspenseProductsByIds({ ids: props.ids.join(','), currency: props.currency });
  const { useGetReviewStatistics } = useReviewService();
  const { data: reviewStats } = useGetReviewStatistics({ productIds: props.ids.join(',') });
  const statsById = createReviewStatsMap(reviewStats);
  if (query.isError) {
    return null;
  }
  const products = orderProductsByKeys(query.data, props.ids, (product) => product.productId).filter(
    (product) => getCartAddProduct(product) !== null,
  );
  return (
    <CartRoutineProducts
      {...props}
      products={products.map((product) => ({ product, rating: statsById[product.productId] }))}
    />
  );
}
